import { useState } from "react";
import { entityKey, RiskOperationalItem } from "./RiskOperationalOverview.types";

// Bulk-mitigate selection lives here, above both the tab-row entry button (in RiskArea) and the operational
// table/dialog, so the button and the table share one source of truth. Mirrors useBulkAssessSelection: keyed by
// type+id so selection persists across pages/filters; "select all" and the header checkbox act on the passed-in
// page items. Only one selection mode (assess or mitigate) is active at a time — RiskArea toggles between them.
export function useBulkMitigateSelection() {
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [isBulkMitigateDialogOpen, setIsBulkMitigateDialogOpen] = useState(false);
    const [selectedEntities, setSelectedEntities] = useState<Map<string, RiskOperationalItem>>(new Map());

    const enterSelectionMode = () => setIsSelectionMode(true);
    const exitSelectionMode = () => {
        setIsSelectionMode(false);
        setSelectedEntities(new Map());
    };

    const isSelected = (item: RiskOperationalItem) => selectedEntities.has(entityKey(item.type, item.entityId));

    const toggleEntity = (item: RiskOperationalItem) => {
        setSelectedEntities((current) => {
            const next = new Map(current);
            const key = entityKey(item.type, item.entityId);
            if (next.has(key)) next.delete(key);
            else next.set(key, item);
            return next;
        });
    };

    const removeSelectedEntity = (key: string) => {
        setSelectedEntities((current) => {
            const next = new Map(current);
            next.delete(key);
            return next;
        });
    };

    const onSelectAllChanged = (pageItems: Array<RiskOperationalItem>) => {
        setSelectedEntities((current) => {
            const next = new Map(current);
            const allPageSelected = pageItems.every((item) => next.has(entityKey(item.type, item.entityId)));
            pageItems.forEach((item) => {
                const key = entityKey(item.type, item.entityId);
                if (allPageSelected) next.delete(key);
                else next.set(key, item);
            });
            return next;
        });
    };

    const getHeaderCheckBoxState = (pageItems: Array<RiskOperationalItem>): "true" | "false" | "indeterminate" => {
        const selectedOnPage = pageItems.filter((item) => selectedEntities.has(entityKey(item.type, item.entityId))).length;
        if (selectedOnPage === 0) return "false";
        return selectedOnPage === pageItems.length ? "true" : "indeterminate";
    };

    return {
        isSelectionMode,
        enterSelectionMode,
        exitSelectionMode,
        isSelected,
        toggleEntity,
        removeSelectedEntity,
        onSelectAllChanged,
        getHeaderCheckBoxState,
        selectedEntities: Array.from(selectedEntities.values()),
        selectedCount: selectedEntities.size,
        isBulkMitigateDialogOpen,
        openBulkMitigateDialog: () => setIsBulkMitigateDialogOpen(true),
        closeBulkMitigateDialog: () => setIsBulkMitigateDialogOpen(false),
    };
}

export type BulkMitigateSelection = ReturnType<typeof useBulkMitigateSelection>;
